import { Inject, Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpResponse
} from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { environment } from '@environments/environment';
import { SESSION_STORAGE } from '../_tokens';
import { AuthModel } from '@modules/auth/models/auth.model';
import { ApiHotelService } from '@donstudio/shared/services/api-hotel.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Injectable()
export class HeaderInterceptor implements HttpInterceptor {
  private readonly apiHotelService = inject(ApiHotelService);

  private HEADER_PATHS = environment.interceptorsPaths.get('header');
  private authSessionStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;
  private AUTH_HEADER = 'Authorization';
  private ACCEPT_HEADER = 'accept';

  constructor(
    @Inject(SESSION_STORAGE) private sessionStorage: Storage,
  ) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const url = req.url.split('/');
    const link = url[url.length - 1];
    const token = this.getAuthFromSessionStorage()?.authToken;
    const sec = this.getAuthFromSessionStorage()?.sec!;


    if (link !== 'login' && token && !this.apiHotelService.isPdfRequest) {
      const modified = req.clone({
        headers: req.headers.set(this.AUTH_HEADER, 'Bearer ' + token).set('x-Sec', sec)
      });
      return next.handle(modified);
    }

    if (link !== 'login' && token && this.apiHotelService.isPdfRequest) {
      const modified = req.clone({
        headers: req.headers.set(this.AUTH_HEADER, 'Bearer ' + token)
          .set('x-Sec', sec)
          .set(this.ACCEPT_HEADER, 'octet-stream/pdf'),
        responseType: 'blob' // Set the responseType to 'blob'
      });

      if (link === 'refreshToken') {
        const modified = req.clone({
          headers: req.headers.set(this.AUTH_HEADER, 'Bearer ' + token).set('x-Sec', sec)
        });
        return next.handle(modified);
      }

      return next.handle(modified).pipe(
        tap((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            if (event.status === 200 && event.body && event.body.type === 'application/octet-stream') {
              if (event.headers.has('content-disposition')) {
                const contentDisposition = event.headers.get('content-disposition');
                const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const match = contentDisposition?.match(regex);
                let fileName = match && match[1].replace(/"/g, '');
                fileName = decodeURIComponent(fileName!);
                this.saveData(event.body, fileName);
              } else {
                this.saveData(event.body);
              }


              this.apiHotelService.isPdfRequest = false;
            }
          }
          return event;
        })
      );
    }

    if (!(this.HEADER_PATHS && this.HEADER_PATHS.test(req.url))) {
      return next.handle(req);
    }

    return next.handle(req);
  }

  private getAuthFromSessionStorage(): AuthModel | undefined {
    try {
      const lsValue = this.sessionStorage.getItem(this.authSessionStorageToken);
      if (!lsValue) {
        return undefined;
      }

      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  private saveData(data: any, fileName?: string) {
    const blob = new Blob([data], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName ? fileName : `report-${new Date().valueOf()}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

}
